import { useId, type KeyboardEvent } from 'react';
import { XIcon } from 'lucide-react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import renderKitCopy from '@/i18n/ns/render-kit';
import { addressTokens, validAddress } from './email-model';

type Props = {
  label: string;
  values: string[];
  pending: string;
  disabled: boolean;
  onChange: (values: string[], pending: string) => void;
};

/** Address chips plus one text field; typed text becomes a chip at a separator. */
export function RecipientField({ label, values, pending, disabled, onChange }: Props) {
  const copy = useNamespaceCopy(renderKitCopy);
  const id = useId();
  const commit = (text: string) => {
    const tokens = addressTokens(text);
    const invalid = tokens.filter((token) => !validAddress(token));
    const valid = tokens.filter((token) => validAddress(token) && !values.includes(token));
    onChange([...values, ...valid], invalid.join(', '));
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
      if (!pending.trim()) return;
      event.preventDefault();
      commit(pending);
    } else if (event.key === 'Backspace' && !pending && values.length) {
      onChange(values.slice(0, -1), '');
    }
  };
  const invalid = addressTokens(pending).find((token) => !validAddress(token));
  return (
    <div className="pc-draft-row" data-invalid={invalid ? 'true' : undefined}>
      <label htmlFor={id}>{label}</label>
      <div className="pc-draft-chips">
        {values.map((value) => (
          <span key={value} className="pc-draft-chip">
            <span>{value}</span>
            <button
              type="button"
              disabled={disabled}
              aria-label={formatCopy(copy.removeRecipient, { address: value })}
              onClick={() =>
                onChange(
                  values.filter((item) => item !== value),
                  pending,
                )
              }
            >
              <XIcon aria-hidden="true" />
            </button>
          </span>
        ))}
        <input
          id={id}
          type="email"
          multiple
          autoComplete="email"
          inputMode="email"
          value={pending}
          disabled={disabled}
          placeholder={values.length ? '' : copy.addRecipient}
          aria-invalid={invalid ? true : undefined}
          onChange={(event) => onChange(values, event.target.value)}
          onInput={(event) => onChange(values, event.currentTarget.value)}
          onKeyDown={onKeyDown}
          onBlur={() => pending.trim() && commit(pending)}
        />
      </div>
      {invalid && (
        <p className="pc-draft-error" role="alert">
          {formatCopy(copy.invalidAddress, { address: invalid })}
        </p>
      )}
    </div>
  );
}
